import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";

// Dynamic Favicon Loader + Scrolling Title
const DynamicFaviconAndTitle = () => {
  useEffect(() => {
    let animationFrameId;
    let faviconImage = null;
    
    const loadFavicon = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/logos`);
        const data = await response.json();
        if (data?.favicon) {
          const faviconUrl = data.favicon.startsWith('http') 
            ? data.favicon 
            : `${process.env.REACT_APP_BACKEND_URL}${data.favicon}`;
          
          // Load the image
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            faviconImage = img;
            startFaviconAnimation();
          };
          img.src = faviconUrl;
        }
      } catch (error) {
        console.log('Favicon not loaded from CMS');
      }
    };
    
    const startFaviconAnimation = () => {
      const canvas = document.createElement('canvas');
      const size = 64;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      let angle = 0;
      
      const animate = () => {
        // Clear canvas
        ctx.clearRect(0, 0, size, size);
        
        const centerX = size / 2;
        const centerY = size / 2;
        const radius = size / 2 - 3;
        
        // Draw the rotating light beam first (behind the logo)
        ctx.save();
        
        // Create rotating gradient for the light beam
        const beamLength = Math.PI * 0.4; // Length of the beam arc
        const startAngle = angle;
        const endAngle = angle + beamLength;
        
        // Draw glowing arc (the light beam)
        for (let i = 3; i >= 0; i--) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius + i, startAngle, endAngle);
          ctx.strokeStyle = `rgba(6, 182, 212, ${0.3 - i * 0.07})`;
          ctx.lineWidth = 4 - i;
          ctx.lineCap = 'round';
          ctx.stroke();
        }
        
        // Draw main bright beam
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        const gradient = ctx.createLinearGradient(
          centerX + Math.cos(startAngle) * radius,
          centerY + Math.sin(startAngle) * radius,
          centerX + Math.cos(endAngle) * radius,
          centerY + Math.sin(endAngle) * radius
        );
        gradient.addColorStop(0, 'rgba(6, 182, 212, 0)');
        gradient.addColorStop(0.5, 'rgba(6, 182, 212, 1)');
        gradient.addColorStop(1, 'rgba(6, 182, 212, 0)');
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();
        
        ctx.restore();
        
        // Draw circular clip for the logo
        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius - 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        
        // Draw the logo image
        if (faviconImage) {
          ctx.drawImage(faviconImage, 2, 2, size - 4, size - 4);
        }
        
        ctx.restore();
        
        // Update rotation angle
        angle += 0.05;
        if (angle > Math.PI * 2) {
          angle = 0;
        }
        
        // Update favicon
        const roundFaviconUrl = canvas.toDataURL('image/png');
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = roundFaviconUrl;
        
        animationFrameId = requestAnimationFrame(animate);
      };
      
      animate();
    };
    
    loadFavicon();
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);
  
  // Scrolling title effect - smooth version
  useEffect(() => {
    const baseTitle = "✨ MSC L'Agence • Création graphique • Vidéo • Web • Marketing digital • ";
    let scrollText = baseTitle + baseTitle; // Double for seamless loop
    let position = 0;
    const visibleLength = 50;
    
    const scrollTitle = () => {
      const displayText = scrollText.substring(position, position + visibleLength);
      document.title = displayText;
      position++;
      if (position >= baseTitle.length) {
        position = 0;
      }
    };
    
    const intervalId = setInterval(scrollTitle, 150);
    
    return () => clearInterval(intervalId);
  }, []);
  
  return null;
};

// Auth Pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";

// Dashboard Pages
import AgencyDashboard from "./pages/dashboard/AgencyDashboard";
import TeamDashboard from "./pages/dashboard/TeamDashboard";
import ClientDashboard from "./pages/dashboard/ClientDashboard";

// Project Pages
import ProjectsPage from "./pages/projects/ProjectsPage";
import ProjectDetailPage from "./pages/projects/ProjectDetailPage";
import NewProjectPage from "./pages/projects/NewProjectPage";

// Wallet Pages
import WalletPage from "./pages/wallet/WalletPage";

// Settings Pages
import ThemeSettingsPage from "./pages/settings/ThemeSettingsPage";
import PromoBannerSettingsPage from "./pages/settings/PromoBannerSettingsPage";
import AxonautIntegrationPage from "./pages/settings/AxonautIntegrationPage";
import AgencyInfoSettingsPage from "./pages/settings/AgencyInfoSettingsPage";

// Admin Pages
import ClientsPage from "./pages/clients/ClientsPage";
import ClientsPageV1 from "./pages/clients/ClientsPageV1";
import CreativesPage from "./pages/creatives/CreativesPage";
import CreativeDirectoryPage from "./pages/creatives/CreativeDirectoryPage";
import CreativeProfilePage from "./pages/creatives/CreativeProfilePage";
import ProjectOffersPage from "./pages/creatives/ProjectOffersPage";
import InvoicesPage from "./pages/invoices/InvoicesPage";
import ExpensesPage from "./pages/expenses/ExpensesPage";

// Profile Page
import ProfilePage from "./pages/profile/ProfilePage";
import TicketsPage from "./pages/tickets/TicketsPage";
import TicketDetailPage from "./pages/tickets/TicketDetailPage";
import CampaignsPage from "./pages/campaigns/CampaignsPage";

// New Pages
import SupportsPage from "./pages/supports/SupportsPage";
import CompanyProfilePage from "./pages/company/CompanyProfilePage";
import AffiliatesPage from "./pages/affiliates/AffiliatesPage";
import AffiliatesPageV1 from "./pages/affiliates/AffiliatesPageV1";
import LinkTrackerPage from "./pages/tools/LinkTrackerPage";
import QRCodePage from "./pages/tools/QRCodePage";
import ContractsPage from "./pages/contracts/ContractsPage";
import ContractDetailPage from "./pages/contracts/ContractDetailPage";
import ContractSubscriptionPage from "./pages/contracts/ContractSubscriptionPage";
// Prospects Pages - V1 Simplified
import ProspectsPageV1 from "./pages/prospects/ProspectsPageV1";
// Keep old ProspectsPage for reference
import ProspectsPage from "./pages/prospects/ProspectsPage";
import PricingSettingsPage from "./pages/settings/PricingSettingsPage";
import NewsSettingsPage from "./pages/settings/NewsSettingsPage";
import KnowledgeBasePage from "./pages/knowledge/KnowledgeBasePage";
import CopiloteDashboard from "./pages/copilote/CopiloteDashboard";
import CopiloteDiscoverPage from "./pages/copilote/CopiloteDiscoverPage";
import CopiloteAdminPage from "./pages/copilote/CopiloteAdminPage";
import TeamPlanningPage from "./pages/planning/TeamPlanningPage";
import NotificationsPage from "./pages/notifications/NotificationsPage";
import CreditsPage from "./pages/credits/CreditsPage";
import SocialDashboardPage from "./pages/social/SocialDashboardPage";
import AssistantPage from "./pages/assistant/AssistantPage";
import WebsiteContentPage from "./pages/website-admin/WebsiteContentPage";
import TeamPortfolioPage from "./pages/website/WebsiteContentPage";
import CopiloteLandingPage from "./pages/website/CopilotePage";
import NosOutilsPage from "./pages/website/NosOutilsPage";
import ToolDetailPage from "./pages/website/ToolDetailPage";
import ContentIngestionPage from "./pages/website-admin/ContentIngestionPage";
import AgencyWebsitePage from "./pages/website/AgencyWebsitePage";
import PageBuilderPage from "./pages/website/PageBuilderPage";
import CaseStudiesPage from "./pages/website/CaseStudiesPage";
import CaseStudyPage from "./pages/website/CaseStudyPage";
import GalleryPage from "./pages/website/GalleryPage";
import QuoteRequestPage from "./pages/website/QuoteRequestPage";
import JoinUsPage from "./pages/website/JoinUsPage";
import CGVPage from "./pages/website/CGVPage";
import BlogPage from "./pages/website/BlogPage";
import PublicKnowledgeBasePage from "./pages/website/KnowledgeBasePage";
import PaymentRequestsPage from "./pages/payments/PaymentRequestsPage";
import TeamPage from "./pages/website/TeamPage";
import PortfolioPage from "./pages/website/PortfolioPage";
import PartnersPage from "./pages/website/PartnersPage";
import BrochurePage from "./pages/website/BrochurePage";
import LegalPage from "./pages/website/LegalPage";
import SEOLandingPage from "./pages/website/SEOLandingPage";
import GameContestServicePage from "./pages/website/GameContestServicePage";
import ScrollToTop from "./components/layout/ScrollToTop";

// Onboarding Pages
import ClientOnboardingPage from "./pages/onboarding/ClientOnboardingPage";

// Admin & Develop Pages (Separated access)
import AdminPage from "./pages/admin/AdminPage";
import DevelopPage from "./pages/develop/DevelopPage";
import TeamOnboardingPage from "./pages/onboarding/TeamOnboardingPage";
import CreativeOnboardingPage from "./pages/onboarding/CreativeOnboardingPage";
import OnboardingManagementPage from "./pages/onboarding/OnboardingManagementPage";

// Auth Pages - Additional
import PendingApprovalPage from "./pages/auth/PendingApprovalPage";

// Client Pages
import ClientReceiptsPage from "./pages/client/ClientReceiptsPage";
import ClientAffiliatePage from "./pages/client/ClientAffiliatePage";

// Receipts Pages
import PendingReceiptsPage from "./pages/receipts/PendingReceiptsPage";
import ReceiptsPageV1 from "./pages/receipts/ReceiptsPageV1";
import AgencyReceiptsPage from "./pages/receipts/AgencyReceiptsPage";
import FormBuilderPage from "./pages/settings/FormBuilderPage";
import TemplatesPage from "./pages/settings/TemplatesPage";
import CreateUserPage from "./pages/users/CreateUserPage";
import SmtpSettingsPage from "./pages/settings/SmtpSettingsPage";

// Affiliate Pages
import AffiliateDashboard from "./pages/affiliate/AffiliateDashboard";
import AffiliateLeadsPage from "./pages/affiliate/AffiliateLeadsPage";
import AffiliateCommissionsPage from "./pages/affiliate/AffiliateCommissionsPage";
import AffiliateContractPage from "./pages/affiliate/AffiliateContractPage";
import CustomerServiceDashboard from "./pages/customer-service/CustomerServiceDashboard";

// New Pages - Reports, Tasks, Offers, Wallet History
import ReportsPage from "./pages/reports/ReportsPage";
import TeamTasksPage from "./pages/tasks/TeamTasksPage";
import OffersPage from "./pages/offers/OffersPage";
import WalletHistoryPage from "./pages/wallet/WalletHistoryPage";
import QuotesManagementPage from "./pages/quotes/QuotesManagementPage";

// Project Requests Pages
import NewProjectRequestPage from "./pages/projects/NewProjectRequestPage";
import MyProjectRequestsPage from "./pages/projects/MyProjectRequestsPage";
import ProjectRequestsManagementPage from "./pages/projects/ProjectRequestsManagementPage";

// Shared Pages (new)
import SharedPasswordsPage from "./pages/shared/SharedPasswordsPage";
import SharedSupportsPage from "./pages/shared/SharedSupportsPage";

// Copilote Coming Soon
import CopiloteComingSoonPage from "./pages/copilote/CopiloteComingSoonPage";

// Promo Banner Admin
import PromoBannerAdminPage from "./pages/admin/PromoBannerAdminPage";

// Admin Layout and Sub-pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminPagesPage from "./pages/admin/AdminPagesPage";
import AdminThemePage from "./pages/admin/AdminThemePage";
import AdminPricingPage from "./pages/admin/AdminPricingPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminPortfolioPage from "./pages/admin/AdminPortfolioPage";
import AdminCaseStudiesPage from "./pages/admin/AdminCaseStudiesPage";
import AdminTeamPage from "./pages/admin/AdminTeamPage";
import AdminGalleryPage from "./pages/admin/AdminGalleryPage";
import AdminSEOPage from "./pages/admin/AdminSEOPage";
import AdminNewsPage from "./pages/admin/AdminNewsPage";
import AdminReviewsPage from "./pages/admin/AdminReviewsPage";
import AdminHeaderPage from "./pages/admin/AdminHeaderPage";
import AdminLegalPage from "./pages/admin/AdminLegalPage";
import AdminHomePage from "./pages/admin/AdminHomePage";
import AdminNewsletterPage from "./pages/admin/AdminNewsletterPage";
import AdminCMSPage from "./pages/admin/AdminCMSPage";

// CMS Pages
import {
  CMSLayout,
  CMSDashboard,
  CMSHeroPage,
  CMSServicesPage,
  CMSTeamPage,
  CMSPortfolioPage,
  CMSPartnersPage,
  CMSTestimonialsPage,
  CMSSocialPage,
  CMSSettingsPage,
  CMSSubscriptionsPage,
  CMSRequestsPage,
  CMSLogosPage,
  CMSSchedulePage,
  CMSMediaPage,
  CMSBlogPage,
  CMSPagesPage,
  CMSGoogleReviewsPage,
  CMSPopupPage,
  CMSTimelinePage,
  CMSTeamPageConfigPage,
  CMSBrochurePage,
  CMSBrochureConfigPage,
  CMSSeoGeneratorPage,
  CMSKeyStatsPage,
  CMSSiteContentPage,
  CMSSmtpConfigPage,
  CMSKnowledgeBasePage,
  CMSImageLocalizationPage,
  CMSGameContestsPage,
  CMSSeoPage
} from "./pages/cms";

// Game Contest Pages (Public - URL partageable uniquement)
import GamePublicPage from "./pages/game/GamePublicPage";
import GamePlayPage from "./pages/game/GamePlayPage";
import GameLoginPage from "./pages/game/GameLoginPage";
import GameRegisterPublicPage from "./pages/game/GameRegisterPublicPage";
import GameRulesPage from "./pages/game/GameRulesPage";
import GamesListPage from "./pages/game/GamesListPage";
import PlayerDashboardPage from "./pages/game/PlayerDashboardPage";
import CoffreMSCPage from "./pages/game/CoffreMSCPage";
import MaProgressionCoffrePage from "./pages/game/MaProgressionCoffrePage";
import TestJourneyPage from "./pages/game/TestJourneyPage";

// Promo Banner Component
import PromoBanner from "./components/layout/PromoBanner";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard
    if (user?.role === 'agency') {
      return <Navigate to="/cms" replace />;
    } else if (user?.role === 'team_member') {
      // Team member affiliates go to affiliate dashboard
      if (user?.team_member_type === 'affiliate') {
        return <Navigate to="/affiliate/dashboard" replace />;
      }
      return <Navigate to="/dashboard/team" replace />;
    } else if (user?.role === 'affiliate') {
      // Direct affiliate role
      return <Navigate to="/affiliate/dashboard" replace />;
    } else if (user?.role === 'customer_service') {
      return <Navigate to="/dashboard/customer-service" replace />;
    } else {
      return <Navigate to="/dashboard/client" replace />;
    }
  }

  return children;
};

// Public Route (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'agency') {
      return <Navigate to="/cms" replace />;
    } else if (user?.role === 'team_member') {
      // Team member affiliates go to affiliate dashboard
      if (user?.team_member_type === 'affiliate') {
        return <Navigate to="/affiliate/dashboard" replace />;
      }
      return <Navigate to="/dashboard/team" replace />;
    } else if (user?.role === 'affiliate') {
      // Direct affiliate role
      return <Navigate to="/affiliate/dashboard" replace />;
    } else if (user?.role === 'customer_service') {
      return <Navigate to="/dashboard/customer-service" replace />;
    } else {
      return <Navigate to="/dashboard/client" replace />;
    }
  }

  return children;
};

// Section guard - redirects to /site when a section is disabled in site settings
const SectionGuard = ({ sectionKey, children }) => {
  const [allowed, setAllowed] = useState(null);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API}/api/cms/site-settings`)
      .then(r => r.json())
      .then(data => setAllowed(data?.[sectionKey]?.enabled === true))
      .catch(() => setAllowed(false));
  }, [sectionKey]);
  if (allowed === null) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  if (!allowed) return <Navigate to="/site" replace />;
  return children;
};

// Home redirect based on role
const HomeRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="spinner" />
      </div>
    );
  }

  // Always redirect to the public site by default
  // If user is logged in and wants to access admin, they use the security icon
  if (!isAuthenticated) {
    return <Navigate to="/site" replace />;
  }

  // Agency users go to the dedicated CMS
  if (user?.role === 'agency') {
    return <Navigate to="/cms" replace />;
  } else if (user?.role === 'team_member') {
    // Team member affiliates go to affiliate dashboard
    if (user?.team_member_type === 'affiliate') {
      return <Navigate to="/affiliate/dashboard" replace />;
    }
    return <Navigate to="/dashboard/team" replace />;
  } else if (user?.role === 'affiliate') {
    // Direct affiliate role
    return <Navigate to="/affiliate/dashboard" replace />;
  } else if (user?.role === 'customer_service') {
    return <Navigate to="/dashboard/customer-service" replace />;
  } else {
    return <Navigate to="/dashboard/client" replace />;
  }
};

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Agency Website */}
        <Route path="/site" element={<AgencyWebsitePage />} />
        <Route path="/site/copilote" element={<SectionGuard sectionKey="copilote_section"><CopiloteLandingPage /></SectionGuard>} />
        <Route path="/site/nos-outils" element={<NosOutilsPage />} />
        <Route path="/site/nos-outils/:slug" element={<ToolDetailPage />} />
        <Route path="/site/equipe" element={<AgencyWebsitePage pageType="equipe" />} />
        <Route path="/site/realisations" element={<AgencyWebsitePage pageType="realisations" />} />
        <Route path="/site/realisations/:slug" element={<CaseStudyPage />} />
        {/* Redirect old project URLs to new case study pages */}
        <Route path="/site/projet/:projectSlug" element={<Navigate to="/site/realisations" replace />} />
        <Route path="/site/brochure" element={<BrochurePage />} />
        <Route path="/site/mentions-legales" element={<LegalPage />} />
        <Route path="/site/confidentialite" element={<LegalPage />} />
        <Route path="/site/cookies" element={<LegalPage />} />
        <Route path="/site/seo/:slug" element={<SEOLandingPage />} />
        <Route path="/site/jeux-concours" element={<SectionGuard sectionKey="game_section"><GameContestServicePage /></SectionGuard>} />
        <Route path="/site/:slug" element={<AgencyWebsitePage />} />

      {/* Game Contest Pages - Protected by game section toggle */}
      <Route path="/le-coffre-msc" element={<SectionGuard sectionKey="game_section"><CoffreMSCPage /></SectionGuard>} />
      <Route path="/ma-progression-coffre" element={<SectionGuard sectionKey="game_section"><MaProgressionCoffrePage /></SectionGuard>} />
      <Route path="/jeux-concours" element={<SectionGuard sectionKey="game_section"><GamesListPage /></SectionGuard>} />
      <Route path="/mon-espace-joueur" element={<SectionGuard sectionKey="game_section"><PlayerDashboardPage /></SectionGuard>} />
      {/* Route test-coffre-msc supprimée en production */}
      <Route path="/jeu/:gameId" element={<SectionGuard sectionKey="game_section"><GamePublicPage /></SectionGuard>} />
      <Route path="/jeu/:gameId/jouer" element={<SectionGuard sectionKey="game_section"><GamePlayPage /></SectionGuard>} />
      <Route path="/jeu/:gameId/connexion" element={<SectionGuard sectionKey="game_section"><GameLoginPage /></SectionGuard>} />
      <Route path="/jeu/:gameId/inscription" element={<SectionGuard sectionKey="game_section"><GameRegisterPublicPage /></SectionGuard>} />
      <Route path="/jeu/:gameId/reglement" element={<SectionGuard sectionKey="game_section"><GameRulesPage /></SectionGuard>} />
      
      {/* CMS Back-Office (dedicated for site management) */}
      <Route
        path="/cms"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <CMSLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CMSDashboard />} />
        <Route path="requests" element={<CMSRequestsPage />} />
        <Route path="hero" element={<CMSHeroPage />} />
        <Route path="services" element={<CMSServicesPage />} />
        <Route path="subscriptions" element={<CMSSubscriptionsPage />} />
        <Route path="team" element={<CMSTeamPage />} />
        <Route path="portfolio" element={<CMSPortfolioPage />} />
        <Route path="partners" element={<CMSPartnersPage />} />
        <Route path="testimonials" element={<CMSTestimonialsPage />} />
        <Route path="social" element={<CMSSocialPage />} />
        <Route path="logos" element={<CMSLogosPage />} />
        <Route path="schedule" element={<CMSSchedulePage />} />
        <Route path="media" element={<CMSMediaPage />} />
        <Route path="blog" element={<CMSBlogPage />} />
        <Route path="knowledge" element={<CMSKnowledgeBasePage />} />
        <Route path="localize-images" element={<CMSImageLocalizationPage />} />
        <Route path="pages" element={<CMSPagesPage />} />
        <Route path="google-reviews" element={<CMSGoogleReviewsPage />} />
        <Route path="popup" element={<CMSPopupPage />} />
        <Route path="timeline" element={<CMSTimelinePage />} />
        <Route path="team-page-config" element={<CMSTeamPageConfigPage />} />
        <Route path="brochure" element={<CMSBrochurePage />} />
        <Route path="brochure-config" element={<CMSBrochureConfigPage />} />
        <Route path="seo-generator" element={<CMSSeoGeneratorPage />} />
        <Route path="key-stats" element={<CMSKeyStatsPage />} />
        <Route path="site-content" element={<CMSSiteContentPage />} />
        <Route path="smtp" element={<CMSSmtpConfigPage />} />
        <Route path="game-contests" element={<CMSGameContestsPage />} />
        <Route path="seo" element={<CMSSeoPage />} />
        <Route path="settings" element={<CMSSettingsPage />} />
      </Route>

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Home redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Agency Dashboard */}
      <Route
        path="/dashboard/agency"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <AgencyDashboard />
          </ProtectedRoute>
        }
      />

      {/* Team Member Dashboard */}
      <Route
        path="/dashboard/team"
        element={
          <ProtectedRoute allowedRoles={['team_member']}>
            <TeamDashboard />
          </ProtectedRoute>
        }
      />

      {/* Client Dashboard */}
      <Route
        path="/dashboard/client"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />

      {/* Customer Service Dashboard */}
      <Route
        path="/dashboard/customer-service"
        element={
          <ProtectedRoute allowedRoles={['customer_service', 'agency']}>
            <CustomerServiceDashboard />
          </ProtectedRoute>
        }
      />

      {/* Affiliate Dashboard */}
      <Route
        path="/dashboard/affiliate"
        element={
          <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
            <AffiliateDashboard />
          </ProtectedRoute>
        }
      />

      {/* Affiliate Pages */}
      <Route
        path="/affiliate/leads"
        element={
          <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
            <AffiliateLeadsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliate/opportunities"
        element={
          <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
            <AffiliateLeadsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliate/commissions"
        element={
          <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
            <AffiliateCommissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliate/contract"
        element={
          <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
            <AffiliateContractPage />
          </ProtectedRoute>
        }
      />

      {/* Affiliate Dashboard alternative route */}
      <Route
        path="/affiliate/dashboard"
        element={
          <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
            <AffiliateDashboard />
          </ProtectedRoute>
        }
      />

      {/* Affiliate Referrals */}
      <Route
        path="/affiliate/referrals"
        element={
          <ProtectedRoute allowedRoles={['affiliate', 'team_member']}>
            <AffiliateLeadsPage />
          </ProtectedRoute>
        }
      />

      {/* Pending Approval Page */}
      <Route path="/pending-approval" element={<PendingApprovalPage />} />

      {/* Projects - All roles can access but with different views */}
      <Route
        path="/projects"
        element={
          <ProtectedRoute allowedRoles={['agency', 'customer_service']}>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute allowedRoles={['agency', 'customer_service', 'team_member', 'client']}>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-projects"
        element={
          <ProtectedRoute allowedRoles={['team_member', 'client']}>
            <ProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-projects/new"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <NewProjectPage />
          </ProtectedRoute>
        }
      />

      {/* Project Requests - Client */}
      <Route
        path="/new-project-request"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <NewProjectRequestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-requests"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <MyProjectRequestsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Project Requests - Agency */}
      <Route
        path="/project-requests"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <ProjectRequestsManagementPage />
          </ProtectedRoute>
        }
      />

      {/* Wallet - Client only */}
      <Route
        path="/wallet"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <WalletPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/wallet"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <WalletPage />
          </ProtectedRoute>
        }
      />

      {/* Client Receipts - Client only */}
      <Route
        path="/my-receipts"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientReceiptsPage />
          </ProtectedRoute>
        }
      />

      {/* Client Affiliate Program - Client only */}
      <Route
        path="/affiliate-program"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientAffiliatePage />
          </ProtectedRoute>
        }
      />

      {/* Profile - All authenticated users */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Settings - Agency only */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <ThemeSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/theme"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <ThemeSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/promo-banner"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <PromoBannerSettingsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Axonaut Integration - Agency only */}
      <Route
        path="/settings/integrations/axonaut"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <AxonautIntegrationPage />
          </ProtectedRoute>
        }
      />
      
      {/* Agency Info Settings - Agency only */}
      <Route
        path="/settings/agency-info"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <AgencyInfoSettingsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Form Builder - Agency only */}
      <Route
        path="/settings/form-builder"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <FormBuilderPage />
          </ProtectedRoute>
        }
      />
      
      {/* Templates Page - Agency only */}
      <Route
        path="/settings/templates"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <TemplatesPage />
          </ProtectedRoute>
        }
      />
      
      {/* Create User Page - Agency only */}
      <Route
        path="/users/create"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <CreateUserPage />
          </ProtectedRoute>
        }
      />
      
      {/* SMTP Settings - Agency only */}
      <Route
        path="/settings/smtp"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <SmtpSettingsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Website Content Management - Agency only */}
      <Route
        path="/settings/website-content"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <WebsiteContentPage />
          </ProtectedRoute>
        }
      />
      
      {/* Team & Portfolio Management - Agency only */}
      <Route
        path="/team-portfolio"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <TeamPortfolioPage />
          </ProtectedRoute>
        }
      />
      
      {/* Content Ingestion / Portfolio Management - Agency only */}
      <Route
        path="/settings/content-manager"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <ContentIngestionPage />
          </ProtectedRoute>
        }
      />

      {/* Clients - Agency and Customer Service */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute allowedRoles={['agency', 'customer_service']}>
            <ClientsPageV1 />
          </ProtectedRoute>
        }
      />

      {/* Creatives - Agency only */}
      <Route
        path="/creatives"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <CreativesPage />
          </ProtectedRoute>
        }
      />
      
      {/* Project Offers - Agency and Team */}
      <Route
        path="/project-offers"
        element={
          <ProtectedRoute allowedRoles={['agency', 'team_member']}>
            <ProjectOffersPage />
          </ProtectedRoute>
        }
      />

      {/* Payment Requests - Agency only */}
      <Route
        path="/payment-requests"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <PaymentRequestsPage />
          </ProtectedRoute>
        }
      />

      {/* Invoices - REMOVED per user request - keeping route to avoid 404 errors */}
      {/* Redirect /invoices to dashboard instead */}
      <Route
        path="/invoices"
        element={<Navigate to="/dashboard" replace />}
      />
      
      {/* Expenses - Agency only */}
      <Route
        path="/expenses"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <ExpensesPage />
          </ProtectedRoute>
        }
      />

      {/* Tickets - Agency, Customer Service and Client */}
      <Route
        path="/tickets"
        element={
          <ProtectedRoute allowedRoles={['agency', 'customer_service', 'client']}>
            <TicketsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tickets/:ticketId"
        element={
          <ProtectedRoute allowedRoles={['agency', 'customer_service', 'client']}>
            <TicketDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Campaigns - Agency only */}
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <CampaignsPage />
          </ProtectedRoute>
        }
      />

      {/* Affiliates - Agency only */}
      <Route
        path="/affiliates"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <AffiliatesPageV1 />
          </ProtectedRoute>
        }
      />

      {/* Supports - Client */}
      <Route
        path="/my-supports"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SupportsPage />
          </ProtectedRoute>
        }
      />

      {/* Company Profile - Client */}
      <Route
        path="/company-profile"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <CompanyProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Link Tracker - Agency */}
      <Route
        path="/tools/link-tracker"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <LinkTrackerPage />
          </ProtectedRoute>
        }
      />

      {/* QR Code Generator - Agency */}
      <Route
        path="/tools/qrcode"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <QRCodePage />
          </ProtectedRoute>
        }
      />

      {/* Contracts - Agency */}
      <Route
        path="/contracts"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <ContractsPage />
          </ProtectedRoute>
        }
      />

      {/* My Contracts - Client */}
      <Route
        path="/my-contracts"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ContractsPage />
          </ProtectedRoute>
        }
      />

      {/* Subscribe to Contract - Client */}
      <Route
        path="/subscribe"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ContractSubscriptionPage />
          </ProtectedRoute>
        }
      />

      {/* Prospects - Agency and Customer Service */}
      <Route
        path="/prospects"
        element={
          <ProtectedRoute allowedRoles={['agency', 'customer_service']}>
            <ProspectsPageV1 />
          </ProtectedRoute>
        }
      />

      {/* Contract Detail */}
      <Route
        path="/contracts/:id"
        element={
          <ProtectedRoute allowedRoles={['agency', 'client']}>
            <ContractDetailPage />
          </ProtectedRoute>
        }
      />

      {/* News Settings - Agency */}
      <Route
        path="/settings/news"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <NewsSettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Pricing Settings - Agency */}
      <Route
        path="/settings/pricing"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <PricingSettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Pending Receipts - Agency & Customer Service */}
      <Route
        path="/receipts/pending"
        element={
          <ProtectedRoute allowedRoles={['agency', 'customer_service']}>
            <ReceiptsPageV1 />
          </ProtectedRoute>
        }
      />
      
      {/* Agency Receipts Management - Agency Only */}
      <Route
        path="/receipts/manage"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <AgencyReceiptsPage />
          </ProtectedRoute>
        }
      />

      {/* Reports - Agency Only */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />

      {/* Team Tasks - Team Member Only */}
      <Route
        path="/my-tasks"
        element={
          <ProtectedRoute allowedRoles={['team_member']}>
            <TeamTasksPage />
          </ProtectedRoute>
        }
      />

      {/* Team Invoices - Team Member Only */}
      <Route
        path="/my-invoices"
        element={
          <ProtectedRoute allowedRoles={['team_member']}>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />

      {/* Offers - Client Only */}
      <Route
        path="/offers"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <OffersPage />
          </ProtectedRoute>
        }
      />

      {/* Wallet History - Client Only */}
      <Route
        path="/wallet/history"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <WalletHistoryPage />
          </ProtectedRoute>
        }
      />

      {/* Quotes Management - Agency Only */}
      <Route
        path="/quotes"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <QuotesManagementPage />
          </ProtectedRoute>
        }
      />

      {/* Knowledge Base - All Users */}
      <Route
        path="/knowledge"
        element={
          <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
            <KnowledgeBasePage />
          </ProtectedRoute>
        }
      />

      {/* Team Planning - Agency Only */}
      <Route
        path="/planning"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <TeamPlanningPage />
          </ProtectedRoute>
        }
      />

      {/* Notifications - All Users */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />

      {/* Profile - All Users */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Credits Suite MSC - All Users */}
      <Route
        path="/credits"
        element={
          <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
            <CreditsPage />
          </ProtectedRoute>
        }
      />

      {/* Social Dashboard - Agency Only */}
      <Route
        path="/social"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <SocialDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* AI Assistant - All Users */}
      <Route
        path="/assistant"
        element={
          <ProtectedRoute allowedRoles={['agency', 'team_member', 'client']}>
            <AssistantPage />
          </ProtectedRoute>
        }
      />

      {/* Page Builder - Agency Only */}
      <Route
        path="/page-builder"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <PageBuilderPage />
          </ProtectedRoute>
        }
      />

      {/* Website Management - Agency Only */}
      <Route
        path="/website/case-studies"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <CaseStudiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/website/gallery"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <GalleryPage />
          </ProtectedRoute>
        }
      />

      {/* Public Quote Request Page */}
      <Route path="/site/devis" element={<QuoteRequestPage />} />
      <Route path="/devis" element={<QuoteRequestPage />} />

      {/* Onboarding Routes */}
      <Route
        path="/onboarding/client"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/team"
        element={
          <ProtectedRoute allowedRoles={['team_member']}>
            <TeamOnboardingPage />
          </ProtectedRoute>
        }
      />
      {/* Public Creative/Affiliate Onboarding */}
      <Route path="/devenir-partenaire" element={<CreativeOnboardingPage />} />
      <Route path="/nous-rejoindre" element={<JoinUsPage />} />
      
      {/* Public Website Pages */}
      <Route path="/site/cgv" element={<CGVPage />} />
      <Route path="/site/mentions-legales" element={<LegalPage />} />
      <Route path="/site/confidentialite" element={<LegalPage />} />
      <Route path="/site/cookies" element={<LegalPage />} />
      <Route path="/site/legal/:slug" element={<LegalPage />} />
      <Route path="/site/blog" element={<BlogPage />} />
      <Route path="/site/blog/:articleSlug" element={<BlogPage />} />
      <Route path="/site/aide" element={<PublicKnowledgeBasePage />} />
      <Route path="/site/aide/:articleSlug" element={<PublicKnowledgeBasePage />} />
      
      {/* Internal Creative Directory (Agency only) */}
      <Route
        path="/annuaire-creatifs"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <CreativeDirectoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creatif/:creativeId"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <CreativeProfilePage />
          </ProtectedRoute>
        }
      />
      
      {/* Onboarding Management (Agency) */}
      <Route
        path="/onboarding-management"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <OnboardingManagementPage />
          </ProtectedRoute>
        }
      />

      {/* COPILOTE Routes - Client Only - Protected by copilote section toggle */}
      <Route
        path="/copilote/discover"
        element={
          <SectionGuard sectionKey="copilote_section">
            <ProtectedRoute allowedRoles={['client']}>
              <CopiloteDiscoverPage />
            </ProtectedRoute>
          </SectionGuard>
        }
      />
      <Route
        path="/copilote/dashboard"
        element={
          <SectionGuard sectionKey="copilote_section">
            <ProtectedRoute allowedRoles={['client']}>
              <CopiloteDashboard />
            </ProtectedRoute>
          </SectionGuard>
        }
      />
      <Route
        path="/copilote/missions"
        element={
          <SectionGuard sectionKey="copilote_section">
            <ProtectedRoute allowedRoles={['client']}>
              <CopiloteDashboard />
            </ProtectedRoute>
          </SectionGuard>
        }
      />
      <Route
        path="/copilote/planning"
        element={
          <SectionGuard sectionKey="copilote_section">
            <ProtectedRoute allowedRoles={['client']}>
              <CopiloteDashboard />
            </ProtectedRoute>
          </SectionGuard>
        }
      />
      <Route
        path="/copilote/reports"
        element={
          <SectionGuard sectionKey="copilote_section">
            <ProtectedRoute allowedRoles={['client']}>
              <CopiloteDashboard />
            </ProtectedRoute>
          </SectionGuard>
        }
      />
      <Route
        path="/copilote/subscription"
        element={
          <SectionGuard sectionKey="copilote_section">
            <ProtectedRoute allowedRoles={['client']}>
              <CopiloteDashboard />
            </ProtectedRoute>
          </SectionGuard>
        }
      />
      
      {/* Copilote Admin - Agency only */}
      <Route
        path="/copilote/admin"
        element={
          <SectionGuard sectionKey="copilote_section">
            <ProtectedRoute allowedRoles={['agency']}>
              <CopiloteAdminPage />
            </ProtectedRoute>
          </SectionGuard>
        }
      />

      {/* Shared Passwords - Agency and Clients */}
      <Route
        path="/shared-passwords"
        element={
          <ProtectedRoute allowedRoles={['agency', 'client']}>
            <SharedPasswordsPage />
          </ProtectedRoute>
        }
      />

      {/* Shared Supports - Agency and Clients */}
      <Route
        path="/shared-supports"
        element={
          <ProtectedRoute allowedRoles={['agency', 'client']}>
            <SharedSupportsPage />
          </ProtectedRoute>
        }
      />

      {/* Copilote Coming Soon - Clients */}
      <Route
        path="/copilote/coming-soon"
        element={
          <SectionGuard sectionKey="copilote_section">
            <ProtectedRoute allowedRoles={['client']}>
              <CopiloteComingSoonPage />
            </ProtectedRoute>
          </SectionGuard>
        }
      />

      {/* Admin Page - Agency only (Portal) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <AdminPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Sub-pages with AdminLayout - Agency only */}
      <Route
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/admin/home" element={<AdminHomePage />} />
        <Route path="/admin/pages" element={<AdminPagesPage />} />
        <Route path="/admin/portfolio" element={<AdminPortfolioPage />} />
        <Route path="/admin/case-studies" element={<AdminCaseStudiesPage />} />
        <Route path="/admin/team" element={<AdminTeamPage />} />
        <Route path="/admin/gallery" element={<AdminGalleryPage />} />
        <Route path="/admin/seo" element={<AdminSEOPage />} />
        <Route path="/admin/news" element={<AdminNewsPage />} />
        <Route path="/admin/reviews" element={<AdminReviewsPage />} />
        <Route path="/admin/pricing" element={<AdminPricingPage />} />
        <Route path="/admin/theme" element={<AdminThemePage />} />
        <Route path="/admin/header" element={<AdminHeaderPage />} />
        <Route path="/admin/promo-banner" element={<PromoBannerAdminPage />} />
        <Route path="/admin/legal" element={<AdminLegalPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/newsletter" element={<AdminNewsletterPage />} />
        <Route path="/admin/cms" element={<AdminCMSPage />} />
      </Route>

      {/* Develop Page - Agency only */}
      <Route
        path="/develop"
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <DevelopPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <DynamicFaviconAndTitle />
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
